import React, { useRef, useState } from 'react';
import { Button, Dropdown, Modal, Table } from 'react-bootstrap';
import { Upload } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { downloadFileFromString } from '../../helpers/global';

const TEMPLATE = 'buyer,buyerEmail,cellPhone,salespersonEmail\n';
const HEADER_KEYS = ['buyer', 'buyeremail', 'cellphone', 'salespersonemail'];

const normalizeHeader = header =>
  String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const parseCsvLine = line => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
};

const readContactsCsv = text => {
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) throw new Error('The CSV file has no contact rows.');

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  if (HEADER_KEYS.some(key => !headers.includes(key))) {
    throw new Error('CSV must include buyer, buyerEmail, cellPhone, and salespersonEmail.');
  }

  const index = {
    buyer: headers.indexOf('buyer'),
    buyerEmail: headers.indexOf('buyeremail'),
    cellPhone: headers.indexOf('cellphone'),
    salespersonEmail: headers.indexOf('salespersonemail')
  };

  return lines
    .slice(1)
    .map(line => {
      const cells = parseCsvLine(line);
      return {
        buyer: cells[index.buyer] || '',
        buyerEmail: cells[index.buyerEmail] || '',
        cellPhone: cells[index.cellPhone] || '',
        salespersonEmail: cells[index.salespersonEmail] || ''
      };
    })
    .filter(row => row.buyer || row.buyerEmail || row.cellPhone || row.salespersonEmail);
};

const UploadContactsCsv = ({ onUploaded, disabled }) => {
  const fileInputRef = useRef(null);
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState(null);

  const onFileChange = async event => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please choose a .csv file.');
      return;
    }

    try {
      const rows = readContactsCsv(await file.text());
      if (!rows.length) {
        toast.error('The CSV file has no contact rows.');
        return;
      }
      fileRef.current = file;
      setContacts(rows);
    } catch (error) {
      toast.error(error.message || 'Could not read the CSV file.');
    }
  };

  const onClose = () => {
    if (saving) return;
    fileRef.current = null;
    setContacts(null);
  };

  const onConfirm = async () => {
    if (!fileRef.current) return;
    const formData = new FormData();
    formData.append('file', fileRef.current);

    setSaving(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_UPLOAD_CSV,
      requestBody: formData,
      stringify: false
    });
    setSaving(false);

    if (error) {
      toast.error(error);
      return;
    }

    onClose();
    if (response.createdCount > 0) {
      toast.success(`Uploaded ${response.createdCount} contact${response.createdCount === 1 ? '' : 's'}.`);
      if (onUploaded) onUploaded();
    }
    if (response.errorCount) {
      toast.error(`${response.errorCount} row${response.errorCount === 1 ? '' : 's'} skipped.`);
    } else if (!response.createdCount) {
      toast.error('No contacts were uploaded.');
    }
  };

  return (
    <>
      <Dropdown className="mb-1 mb-md-0">
        <Dropdown.Toggle size="sm" variant="primary" block disabled={disabled || saving} id="upload-contacts-csv">
          <Upload className="align-text-top mr-1" size={14} />
          Upload CSV
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => fileInputRef.current.click()}>Upload file</Dropdown.Item>
          <Dropdown.Item
            onClick={() => downloadFileFromString({ data: TEMPLATE, fileName: 'contacts-upload-template.csv' })}
          >
            Download template
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={onFileChange} />
      <Modal show={contacts != null} onHide={onClose} centered size="lg" backdrop="static">
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Contact CSV upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            {contacts?.length || 0} contact{contacts?.length === 1 ? '' : 's'} ready to upload.
          </p>
          {contacts?.length > 0 && (
            <div style={{ maxHeight: 320, overflow: 'auto' }}>
              <Table bordered size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>buyer</th>
                    <th>buyerEmail</th>
                    <th>cellPhone</th>
                    <th>salespersonEmail</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(contact => (
                    <tr key={`${contact.buyerEmail}-${contact.cellPhone}-${contact.buyer}`}>
                      <td>{contact.buyer}</td>
                      <td>{contact.buyerEmail}</td>
                      <td>{contact.cellPhone}</td>
                      <td>{contact.salespersonEmail}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm} disabled={saving || !contacts?.length}>
            {saving ? 'Uploading...' : 'Upload'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UploadContactsCsv;
